import type { CaseStudy } from '../../types/content'

export const northpulseDashboard: CaseStudy = {
  slug: 'northpulse-dashboard',
  summary:
    'A self-initiated concept for a reporting tool aimed at founders rather than analysts. We designed for the ninety seconds someone actually spends in a dashboard, not the hour a demo assumes.',
  overview: {
    industry: 'Concept · B2B SaaS analytics',
    location: 'Concept · India',
    timeline: 'Self-initiated concept, 2026',
    services: ['Product strategy', 'Dashboard UI design', 'Design system', 'Prototype'],
  },
  problem: {
    heading: 'The problem',
    paragraphs: [
      'Northpulse does not exist. This is concept work we built to test a hypothesis about analytics products, and we are labelling it as such because pretending otherwise would be the fastest way to lose your trust.',
      'The hypothesis: analytics tools are designed for the person who configures them, not the person who checks them. A founder opens a dashboard between two meetings, looks for one number, decides whether anything is on fire, and leaves. Most products answer that in the fourth widget down, after three cards of vanity metrics and a chart with a date range nobody set.',
      'The result is a familiar failure. Teams pay for a reporting product, configure it once, and revert to a spreadsheet somebody exports on Mondays. The tool is not too simple or too complex — it is aimed at the wrong ninety seconds.',
    ],
  },
  research: {
    heading: 'Assumptions and discovery',
    paragraphs: [
      'This is concept work, so none of the following is a research finding. These are the assumptions the design was built against, drawn from publicly available product tours and our own use of these tools rather than from a study we ran.',
      'We reviewed the default state of several analytics products at a comparable subscription tier — what the dashboard shows before anyone configures it. The pattern we assume is consistent: around a dozen metrics on the first screen, a chart opening on a date range the user did not choose, and nothing telling them whether anything has changed enough to care about.',
      'The second assumption concerns the shape of a session. We designed for a check-in of roughly ninety seconds: open, compare one or two numbers against a rough expectation, and only dig further if something looks wrong. That is the session we think products under-serve.',
      'The third is that the number that matters is personal and stable — trial-to-paid conversion for one operator, cash runway for another, weekly active teams for a third — and that no product makes it the first thing on the page. If that assumption is wrong, the whole concept is wrong, which is why onboarding asks the question directly.',
      'From those assumptions we wrote an inventory of what a check-in session actually needs: the number, the direction of travel, whether the movement is outside normal variance, and one route into the detail. Four things, not eleven.',
    ],
  },
  artifacts: [
    {
      kind: 'audit',
      title: 'Default state review',
      caption: 'Comparable products reviewed at signup, metrics counted',
    },
    {
      kind: 'inventory',
      title: 'Ninety-second session inventory',
      caption: 'The four things a check-in session actually requires',
    },
    {
      kind: 'flow',
      title: 'Answer-first navigation',
      caption: 'Headline number to detail view in one interaction',
    },
  ],
  strategy: {
    heading: 'Strategy and decisions',
    paragraphs: [
      'The strategy is one commitment: the product must answer the user’s standing question before it offers anything else. Onboarding therefore has exactly one job — find out what that question is — and the whole interface is arranged around the answer.',
      'That produces a shape most analytics products do not have. A single headline figure at editorial scale, its movement stated in plain language rather than a coloured arrow, a variance note that says whether the change is meaningful, and then — below the fold, deliberately — the supporting metrics and the table.',
      'The second commitment is that the tool must be honest about uncertainty. If a number is based on partial data or an incomplete day, the interface says so in words instead of quietly rendering a dip that starts a fire drill. That single behaviour is the part of the concept we would most want tested first.',
    ],
  },
  decisions: [
    {
      decision: 'Made onboarding ask one question: what number do you check?',
      reasoning:
        'We assume most operators can name their number instantly. Asking for it takes one screen and should make every subsequent session shorter.',
    },
    {
      decision: 'Put a single figure above the fold and the grid below it',
      reasoning:
        'A grid of equal-weight tiles is an unmade decision. The check-in session needs one answer, so one number gets the scale and everything else waits.',
    },
    {
      decision: 'Replaced coloured arrows with a written variance note',
      reasoning:
        'Green-up and red-down cannot say "this is within normal weekly variation". A sentence can, and it prevents the wrong meeting.',
    },
    {
      decision: 'Designed the incomplete-data state before the healthy one',
      reasoning:
        'Partial days are the most common cause of false alarms in analytics tools. Designing that state first stopped it from becoming an afterthought.',
    },
  ],
  design: {
    heading: 'Design direction',
    paragraphs: [
      'The interface is light, quiet and almost entirely typographic. A white working surface, one ink for text, hairline borders instead of cards wherever grouping allows, and a single blue reserved for the active state and the primary action. Nothing else in the product is coloured, which means a coloured pixel always carries meaning.',
      'Two type sizes carry the whole hierarchy in the data layer, and one oversized numeral treatment carries the headline figure. Numbers are set in tabular figures throughout so columns align and a changing value does not shift the layout — a small thing that makes a product feel engineered.',
      'The system is twelve components: a headline metric block, a variance note, a stat tile, a line chart, a data table row, a filter bar, an empty state, a loading skeleton, a partial-data banner, a side panel, a command menu and a toast. Each has a defined loading, empty and error state, because in an analytics product those are not edge cases — they are Monday morning.',
    ],
  },
  typefaces: [
    { name: 'Inter Tight', role: 'Entire interface, two sizes plus the headline numeral' },
    { name: 'Inter Tight, tabular', role: 'All numeric data and table columns' },
  ],
  palette: [
    { name: 'Surface', hex: '#FFFFFF' },
    { name: 'Ink', hex: '#0E0E0E' },
    { name: 'Signal blue', hex: '#2F4BFF' },
    { name: 'Hairline', hex: '#E6E6E2' },
  ],
  components: [
    'Headline metric block',
    'Written variance note',
    'Stat tile',
    'Line chart',
    'Data table row',
    'Partial-data banner',
    'Empty and loading states',
    'Command menu',
  ],
  mockupCaption: 'Dashboard, detail panel and onboarding, desktop and mobile',
  results: {
    isProjected: true,
    intro:
      'This is self-initiated concept work. Nothing here has shipped and no figures have been measured in market. The following are the targets we designed against, stated as expected impact rather than results.',
    metrics: [
      { value: 'Under 5s', label: 'Target time to first answer' },
      { value: '1 question', label: 'Onboarding reduced to a single decision' },
      { value: '4 of 11', label: 'Default metrics kept above the fold' },
      { value: '12', label: 'Components, each with three states' },
    ],
    note:
      'Built as a clickable prototype, designed so an operator can find the number in under five seconds. Nothing here has been tested with users or shipped — it is a design intent, not evidence.',
  },
  quote: {
    text:
      'We prototype ideas like this to find out whether a strong opinion holds up once it is drawn. It is the cheapest way to argue with yourself before anyone pays for the answer.',
    name: 'ShreyasStudio',
    role: 'Self-initiated concept, 2026',
  },
}
